// Mock exercise content for the /mockup/student lesson player. Purely illustrative —
// swap for DialogApi / DialogPromptApi content when wiring for real.
//
// Every kind below stands for a DialogPrompt subclass a student actually meets
// inside a lesson today — see getClassNameDescription() in
// component/dialogPrompt/DialogPromptForm.tsx for the authoritative list. Each
// interface names the class it mirrors. The settings-style prompts (Select
// Teacher / Avatar / Audio Speed / Scoring Level / Learn Characters / Enter Name
// / Start class / Feedback button) are not exercises; they live on the student
// login, profile and feedback screens instead.

import { StudentLesson } from './mockData';

export type SoundGrade = 'good' | 'ok' | 'bad';

export interface CharacterGrade {
    character: string;
    pinyinExpected: string;
    pinyinSpoken: string;
    initial: SoundGrade;
    final: SoundGrade;
    tone: SoundGrade;
    // undefined ≡ 'speech'. 'silence' = expected sound not heard; 'extra' = inserted sound.
    alignment?: 'speech' | 'silence' | 'extra';
}

const soundGradeByLetter: Record<string, SoundGrade> = { g: 'good', o: 'ok', b: 'bad' };

// Compact authoring for per-character grades: one "|"-separated entry per
// character, written as "<character> <expected pinyin> <initial><final><tone>
// [spoken pinyin]" with each grade letter g(ood) / o(k) / b(ad). The spoken
// pinyin defaults to the expected one.
function charGrades(spec: string): CharacterGrade[] {
    return spec.split('|').map(entry => {
        const [character, pinyinExpected, letters, pinyinSpoken] = entry.trim().split(/\s+/);
        return {
            character,
            pinyinExpected,
            pinyinSpoken: pinyinSpoken ?? pinyinExpected,
            initial: soundGradeByLetter[letters[0]],
            final: soundGradeByLetter[letters[1]],
            tone: soundGradeByLetter[letters[2]],
        };
    });
}

interface ExerciseBase {
    id: string;
}

// DialogPromptImageModel — "Dialog w/ Image": a picture plus a teacher line,
// nothing to answer. The emoji stands in for image_src_url; leave it out and the
// card is a plain DialogPromptModel ("Dialog Only") teacher line.
export interface ImageExercise extends ExerciseBase {
    kind: 'image';
    emoji?: string;
    caption: string;
    chineseHanzi?: string;
    chinesePinyin?: string;
}

// DialogPromptIntroduceWordModel — "Dialog w/ Character Listen".
export interface WordIntroExercise extends ExerciseBase {
    kind: 'word';
    hanzi: string;
    pinyin: string;
    meaning: string;
    emoji: string;
    example: { hanzi: string; pinyin: string; english: string };
}

// DialogPromptListenSpeakModel / DialogPromptListenSpeakNoGradeModel —
// "Dialog w/ Listen & Speak" and its ungraded twin: hear the line, say it back.
export interface ListenRepeatExercise extends ExerciseBase {
    kind: 'listenRepeat';
    hanzi: string;
    pinyin: string;
    english: string;
    infoText: string;
    graded: boolean;
    grades: CharacterGrade[];
}

// DialogPromptQuizQuestionModel ('text' / 'picture'), DialogPromptQuizQuestionAudioModel
// ('audio') and DialogPromptQuizQuestionSpeakModel (any mode plus speakAfter) —
// "Quiz: Select One", "…Select One Audio", "…Select One, then Speak".
export interface QuizExercise extends ExerciseBase {
    kind: 'quiz';
    mode: 'text' | 'picture' | 'audio';
    prompt: string;
    imageEmoji?: string;
    options: { hanzi: string; pinyin: string }[];
    correctIndex: number;
    speakAfter?: { instruction: string; grades: CharacterGrade[] };
}

// DialogPromptQuizWordModel — "Quiz: Character Recognition". The teacher says a
// word; the student picks its characters out of a row of look-alikes.
export interface QuizWordExercise extends ExerciseBase {
    kind: 'quizWord';
    prompt: string;
    answer: { hanzi: string; pinyin: string };
    distractors: { hanzi: string; pinyin: string }[];
}

export type MatchSide = 'character' | 'pinyin' | 'english' | 'image' | 'audio';

// DialogPromptMatchGameModel — "Quiz: Matching Game". left_match / right_match
// pick what each column shows. Also covers the deprecated
// DialogPromptMatchWordPictureModel ("Quiz: Matching on Vocabulary"), which is
// this game locked to character ↔ image.
export interface MatchGameExercise extends ExerciseBase {
    kind: 'match';
    prompt: string;
    leftMatch: MatchSide;
    rightMatch: MatchSide;
    pairs: { hanzi: string; pinyin: string; english: string; emoji: string }[];
}

// DialogPromptImageSelectSpeakModel — "Dialog w/ Image Select & Speak". Any
// picture is a valid pick; the student then says a sentence about it.
export interface ImageSelectSpeakExercise extends ExerciseBase {
    kind: 'imageSelectSpeak';
    prompt: string;
    chineseHanzi: string;
    chinesePinyin: string;
    // The sentence the picked word drops into; "{}" marks the slot.
    answerFrame: { hanzi: string; english: string };
    options: { emoji: string; hanzi: string; pinyin: string; english: string }[];
    grades: CharacterGrade[];
}

// DialogPromptTypeSpeakModel / DialogPromptTypeSpeakV2Model — "Prompt, Type,
// Speak". The typed answer is checked (and corrected) before it is spoken.
export interface TypeSpeakExercise extends ExerciseBase {
    kind: 'typeSpeak';
    chineseHanzi: string;
    chinesePinyin: string;
    english: string;
    instruction: string;
    suggestion: { hanzi: string; pinyin: string };
    correctionNote: string;
    grades: CharacterGrade[];
}

// DialogPromptSpeakWithGradeModel ("Prompt, Speak With Grade") and
// DialogPromptSpeakNoGradeModel ("Prompt, Speak Anything, No Grade").
export interface SpeakingExercise extends ExerciseBase {
    kind: 'speak';
    graded: boolean;
    heroHanzi: string;
    heroPinyin: string;
    speaker: { nameHanzi: string; nameEnglish: string };
    prompt: { hanzi: string; english: string };
    starters: string[];
    hint?: string;
    grades: CharacterGrade[];
}

export interface ConversationLine {
    speakerHanzi: string;
    speakerEnglish: string;
    hanzi: string;
    pinyin: string;
    english: string;
    side: 'left' | 'right';
    // The student takes this line in every speaking mode.
    isStudent?: boolean;
    // sentence_text_alternate for 'readBlankSpeak', with the gaps already filled in.
    blankHanzi?: string;
    grades?: CharacterGrade[];
}

// One card per conversation prompt class:
//   listen             → DialogPromptConversationListenModel
//   listenNoCharacters → DialogPromptConversationListenWithoutCharacterModel
//   listenSpeak        → DialogPromptConversationListenSpeakModel
//   readSpeak          → DialogPromptConversationReadSpeakModel
//   readBlankSpeak     → DialogPromptConversationReadWithBlankSpeakModel
//   speakOnly          → DialogPromptConversationSpeakOnlyModel
export type ConversationMode = 'listen' | 'listenNoCharacters' | 'listenSpeak' | 'readSpeak' | 'readBlankSpeak' | 'speakOnly';

export interface ConversationExercise extends ExerciseBase {
    kind: 'conversation';
    mode: ConversationMode;
    title: string;
    lines: ConversationLine[];
}

// DialogPromptSummaryModel — "Speaking Summary": every sentence the student
// spoke this lesson, newest attempt first, earlier tries tucked underneath.
export interface SummaryExercise extends ExerciseBase {
    kind: 'summary';
    title: string;
    sentences: { english: string; attempts: CharacterGrade[][] }[];
}

// DialogPromptSelectNextDialogModel — "Select next dialog": the student picks
// where the lesson goes next.
export interface ChooseNextExercise extends ExerciseBase {
    kind: 'chooseNext';
    prompt: string;
    options: { emoji: string; title: string; detail: string }[];
}

export type LessonExercise =
    | ImageExercise
    | WordIntroExercise
    | ListenRepeatExercise
    | QuizExercise
    | QuizWordExercise
    | MatchGameExercise
    | ImageSelectSpeakExercise
    | TypeSpeakExercise
    | SpeakingExercise
    | ConversationExercise
    | SummaryExercise
    | ChooseNextExercise;

// ---------------------------------------------------------------------------
// Authored content: lesson 201 "Ordering at a Restaurant" — mirrors the lesson
// the teacher builds in the /teacher mockup (teacher/mockLesson.ts), so both
// mockups demo the same lesson from each side.
// ---------------------------------------------------------------------------

const restaurantConversationLines: ConversationLine[] = [
    {
        speakerHanzi: '服务员', speakerEnglish: 'Waiter', side: 'left',
        hanzi: '您好！欢迎光临。您想吃什么？', pinyin: 'Nín hǎo! Huānyíng guānglín. Nín xiǎng chī shénme?',
        english: 'Hello! Welcome. What would you like to eat?',
    },
    {
        speakerHanzi: '大伟', speakerEnglish: 'Dawei', side: 'right',
        hanzi: '请给我一盘饺子和一碗米饭。', pinyin: 'Qǐng gěi wǒ yì pán jiǎozi hé yì wǎn mǐfàn.',
        english: 'Please give me a plate of dumplings and a bowl of rice.',
        isStudent: true,
        blankHanzi: '请给我一盘__和一碗__。',
        grades: charGrades('请 qǐng ggg | 给 gěi ggg | 我 wǒ ggg | 一 yì ggo yí | 盘 pán ggg | 饺 jiǎo ggg | 子 zi gog | 和 hé ggo | 一 yì ggg | 碗 wǎn ggb wán | 米 mǐ ggg | 饭 fàn ggg'),
    },
    {
        speakerHanzi: '服务员', speakerEnglish: 'Waiter', side: 'left',
        hanzi: '好的。您想喝什么？', pinyin: 'Hǎo de. Nín xiǎng hē shénme?',
        english: 'Sure. What would you like to drink?',
    },
    {
        speakerHanzi: '大伟', speakerEnglish: 'Dawei', side: 'right',
        hanzi: '请给我一杯茶，谢谢！', pinyin: 'Qǐng gěi wǒ yì bēi chá, xièxie!',
        english: 'Please give me a cup of tea, thank you!',
        isStudent: true,
        blankHanzi: '请给我一杯__，谢谢！',
        grades: charGrades('请 qǐng ggg | 给 gěi ggg | 我 wǒ ggg | 一 yì ggg | 杯 bēi gog bei | 茶 chá gob chā | 谢 xiè ggo | 谢 xie ggg'),
    },
];

const orderingAtRestaurantExercises: LessonExercise[] = [
    {
        id: '201-image-restaurant',
        kind: 'image',
        emoji: '🍽️',
        caption: 'Today we are eating out. Look at the restaurant before we order.',
        chineseHanzi: '我们去饭馆吧！',
        chinesePinyin: 'Wǒmen qù fànguǎn ba!',
    },
    {
        id: '201-word-caidan',
        kind: 'word',
        hanzi: '菜单',
        pinyin: 'càidān',
        meaning: 'menu',
        emoji: '📋',
        example: { hanzi: '请给我菜单。', pinyin: 'Qǐng gěi wǒ càidān.', english: 'Please give me the menu.' },
    },
    {
        id: '201-word-fuwuyuan',
        kind: 'word',
        hanzi: '服务员',
        pinyin: 'fúwùyuán',
        meaning: 'server',
        emoji: '🤵',
        example: { hanzi: '服务员，请给我一杯茶。', pinyin: 'Fúwùyuán, qǐng gěi wǒ yì bēi chá.', english: 'Server, please give me a cup of tea.' },
    },
    {
        id: '201-listen-caidan',
        kind: 'listenRepeat',
        hanzi: '请给我菜单。',
        pinyin: 'Qǐng gěi wǒ càidān.',
        english: 'Please give me the menu.',
        infoText: 'Listen once, then say it back with the same tones.',
        graded: true,
        grades: charGrades('请 qǐng ggg | 给 gěi ggg | 我 wǒ ggg | 菜 cài ggo | 单 dān ggb dǎn'),
    },
    {
        id: '201-word-jiaozi',
        kind: 'word',
        hanzi: '饺子',
        pinyin: 'jiǎozi',
        meaning: 'dumplings',
        emoji: '🥟',
        example: { hanzi: '我要一盘饺子。', pinyin: 'Wǒ yào yì pán jiǎozi.', english: 'I want a plate of dumplings.' },
    },
    {
        id: '201-quiz-jiaozi-pic',
        kind: 'quiz',
        mode: 'picture',
        prompt: 'Which word matches the picture?',
        imageEmoji: '🥟',
        options: [
            { hanzi: '米饭', pinyin: 'mǐfàn' },
            { hanzi: '饺子', pinyin: 'jiǎozi' },
            { hanzi: '菜单', pinyin: 'càidān' },
            { hanzi: '服务员', pinyin: 'fúwùyuán' },
        ],
        correctIndex: 1,
    },
    {
        id: '201-word-mifan',
        kind: 'word',
        hanzi: '米饭',
        pinyin: 'mǐfàn',
        meaning: 'cooked rice',
        emoji: '🍚',
        example: { hanzi: '请给我一碗米饭。', pinyin: 'Qǐng gěi wǒ yì wǎn mǐfàn.', english: 'Please give me a bowl of rice.' },
    },
    {
        id: '201-quizword-mifan',
        kind: 'quizWord',
        prompt: 'Listen, then tap the word you heard.',
        answer: { hanzi: '米饭', pinyin: 'mǐfàn' },
        distractors: [
            { hanzi: '面条', pinyin: 'miàntiáo' },
            { hanzi: '米线', pinyin: 'mǐxiàn' },
            { hanzi: '饭馆', pinyin: 'fànguǎn' },
        ],
    },
    {
        id: '201-quiz-mifan-pic',
        kind: 'quiz',
        mode: 'picture',
        prompt: 'Which phrase matches the picture?',
        imageEmoji: '🍚',
        options: [
            { hanzi: '一盘饺子', pinyin: 'yì pán jiǎozi' },
            { hanzi: '一杯茶', pinyin: 'yì bēi chá' },
            { hanzi: '一碗米饭', pinyin: 'yì wǎn mǐfàn' },
            { hanzi: '一盘米饭', pinyin: 'yì pán mǐfàn' },
        ],
        correctIndex: 2,
    },
    {
        id: '201-match-dishes',
        kind: 'match',
        prompt: 'Match each word with its picture.',
        leftMatch: 'character',
        rightMatch: 'image',
        pairs: [
            { hanzi: '菜单', pinyin: 'càidān', english: 'menu', emoji: '📋' },
            { hanzi: '饺子', pinyin: 'jiǎozi', english: 'dumplings', emoji: '🥟' },
            { hanzi: '米饭', pinyin: 'mǐfàn', english: 'cooked rice', emoji: '🍚' },
            { hanzi: '茶', pinyin: 'chá', english: 'tea', emoji: '🍵' },
        ],
    },
    {
        id: '201-quiz-audio-jiaozi',
        kind: 'quiz',
        mode: 'audio',
        prompt: 'Play each order. Which one matches the picture?',
        imageEmoji: '🥟',
        options: [
            { hanzi: '请给我一碗米饭。', pinyin: 'Qǐng gěi wǒ yì wǎn mǐfàn.' },
            { hanzi: '请给我一杯茶。', pinyin: 'Qǐng gěi wǒ yì bēi chá.' },
            { hanzi: '请给我一盘饺子。', pinyin: 'Qǐng gěi wǒ yì pán jiǎozi.' },
            { hanzi: '请给我菜单。', pinyin: 'Qǐng gěi wǒ càidān.' },
        ],
        correctIndex: 2,
    },
    {
        id: '201-quiz-beicha',
        kind: 'quiz',
        mode: 'text',
        prompt: "Which one means 'a cup of tea'?",
        options: [
            { hanzi: '一碗茶', pinyin: 'yì wǎn chá' },
            { hanzi: '一盘茶', pinyin: 'yì pán chá' },
            { hanzi: '一杯茶', pinyin: 'yì bēi chá' },
            { hanzi: '一杯饺子', pinyin: 'yì bēi jiǎozi' },
        ],
        correctIndex: 2,
    },
    {
        id: '201-quiz-speak-order',
        kind: 'quiz',
        mode: 'text',
        prompt: "Which one means 'Please give me a plate of dumplings'?",
        options: [
            { hanzi: '请给我一碗饺子。', pinyin: 'Qǐng gěi wǒ yì wǎn jiǎozi.' },
            { hanzi: '请给我一盘饺子。', pinyin: 'Qǐng gěi wǒ yì pán jiǎozi.' },
            { hanzi: '请给我一杯饺子。', pinyin: 'Qǐng gěi wǒ yì bēi jiǎozi.' },
        ],
        correctIndex: 1,
        speakAfter: {
            instruction: '现在请你说一遍。Now say it out loud.',
            grades: charGrades('请 qǐng ggg | 给 gěi ggg | 我 wǒ ggg | 一 yì ggo yí | 盘 pán ggb pān | 饺 jiǎo ggo | 子 zi gog'),
        },
    },
    {
        id: '201-imageselect-order',
        kind: 'imageSelectSpeak',
        prompt: 'Pick anything you like, then order it.',
        chineseHanzi: '您想吃什么？',
        chinesePinyin: 'Nín xiǎng chī shénme?',
        answerFrame: { hanzi: '请给我{}。', english: 'Please give me {}.' },
        options: [
            { emoji: '🥟', hanzi: '一盘饺子', pinyin: 'yì pán jiǎozi', english: 'a plate of dumplings' },
            { emoji: '🍚', hanzi: '一碗米饭', pinyin: 'yì wǎn mǐfàn', english: 'a bowl of rice' },
            { emoji: '🍜', hanzi: '一碗面条', pinyin: 'yì wǎn miàntiáo', english: 'a bowl of noodles' },
            { emoji: '🍵', hanzi: '一杯茶', pinyin: 'yì bēi chá', english: 'a cup of tea' },
        ],
        grades: charGrades('请 qǐng ggg | 给 gěi ggg | 我 wǒ ggg | 一 yì ggg | 盘 pán ggo | 饺 jiǎo ggo | 子 zi gog'),
    },
    {
        id: '201-convo-restaurant',
        kind: 'conversation',
        mode: 'listen',
        title: 'Ordering at a restaurant',
        lines: restaurantConversationLines,
    },
    {
        id: '201-convo-restaurant-blank',
        kind: 'conversation',
        mode: 'readBlankSpeak',
        title: 'Fill the gaps out loud',
        lines: restaurantConversationLines,
    },
    {
        id: '201-typespeak-drink',
        kind: 'typeSpeak',
        chineseHanzi: '您想喝什么？',
        chinesePinyin: 'Nín xiǎng hē shénme?',
        english: 'What would you like to drink?',
        instruction: 'Type your answer, then say it out loud.',
        suggestion: { hanzi: '请给我一杯茶。', pinyin: 'Qǐng gěi wǒ yì bēi chá.' },
        correctionNote: 'Almost — a cup of tea takes the measure word 杯: 请给我一杯茶。',
        grades: charGrades('请 qǐng ggg | 给 gěi ggg | 我 wǒ ggg | 一 yì ggg | 杯 bēi gog bei | 茶 chá gob chā'),
    },
    {
        id: '201-speak-order',
        kind: 'speak',
        graded: true,
        heroHanzi: '你准备好点菜了吗？',
        heroPinyin: 'Nǐ zhǔnbèi hǎo diǎn cài le ma?',
        speaker: { nameHanzi: '服务员', nameEnglish: 'Waiter' },
        prompt: { hanzi: '您好！欢迎光临。您想吃什么？', english: 'Hello! Welcome. What would you like to eat?' },
        starters: ['请给我一盘……', '我要一碗……', '你们有什么……？'],
        grades: charGrades('请 qǐng ggg | 给 gěi ggg | 我 wǒ ggg | 一 yì ggo yí | 盘 pán ggb pān | 饺 jiǎo ggo | 子 zi gog | 和 hé ggo | 一 yì ggg | 杯 bēi gog bei | 茶 chá gob chā'),
    },
    {
        id: '201-speak-exit',
        kind: 'speak',
        graded: true,
        heroHanzi: '该你点菜了！',
        heroPinyin: 'Gāi nǐ diǎn cài le!',
        speaker: { nameHanzi: '服务员', nameEnglish: 'Waiter' },
        prompt: { hanzi: '您想吃什么？', english: 'What would you like to eat?' },
        starters: [],
        hint: 'Remember the measure word: 一碗 for rice, 一盘 for dumplings.',
        grades: charGrades('请 qǐng ggg | 给 gěi ggg | 我 wǒ ggg | 一 yì ggg | 碗 wǎn ggb wán | 米 mǐ ggo | 饭 fàn ggg | 谢 xiè ggo | 谢 xie ggg'),
    },
];

// ---------------------------------------------------------------------------
// Authored content: lesson 202 "Weekend Plans" (occupies the LAST positions of
// the lesson so the resume point lands right at the start of this arc).
// ---------------------------------------------------------------------------

const weekendConversationLines: ConversationLine[] = [
    {
        speakerHanzi: '小美', speakerEnglish: 'Xiaomei', side: 'left',
        hanzi: '这个周末你想做什么？', pinyin: 'Zhège zhōumò nǐ xiǎng zuò shénme?',
        english: 'What do you want to do this weekend?',
    },
    {
        speakerHanzi: '大伟', speakerEnglish: 'Dawei', side: 'right',
        hanzi: '我想去看电影。你呢？', pinyin: 'Wǒ xiǎng qù kàn diànyǐng. Nǐ ne?',
        english: 'I want to go see a movie. How about you?',
        isStudent: true,
        blankHanzi: '我想去__电影。你呢？',
        grades: charGrades('我 wǒ ggg | 想 xiǎng ggb xiáng | 去 qù gog | 看 kàn ggg | 电 diàn ggg | 影 yǐng gbo yǐn | 你 nǐ ggg | 呢 ne ggg'),
    },
    {
        speakerHanzi: '小美', speakerEnglish: 'Xiaomei', side: 'left',
        hanzi: '我打算去公园跑步。', pinyin: 'Wǒ dǎsuàn qù gōngyuán pǎobù.',
        english: 'I plan to go running in the park.',
    },
    {
        speakerHanzi: '大伟', speakerEnglish: 'Dawei', side: 'right',
        hanzi: '好，那星期天我们一起吃晚饭吧！', pinyin: 'Hǎo, nà xīngqītiān wǒmen yìqǐ chī wǎnfàn ba!',
        english: "OK, then let's have dinner together on Sunday!",
        isStudent: true,
        blankHanzi: '好，那星期天我们__吃晚饭吧！',
        grades: charGrades('好 hǎo ggg | 那 nà ggg | 星 xīng ggg | 期 qī ggo | 天 tiān ggg | 我 wǒ ggg | 们 men ggg | 一 yì ggo yí | 起 qǐ ggg | 吃 chī gob chi | 晚 wǎn ggg | 饭 fàn ggg | 吧 ba ggg'),
    },
];

const weekendPlansExercises: LessonExercise[] = [
    {
        id: '202-word-zhoumo',
        kind: 'word',
        hanzi: '周末',
        pinyin: 'zhōumò',
        meaning: 'weekend',
        emoji: '🗓️',
        example: { hanzi: '周末你有空吗？', pinyin: 'Zhōumò nǐ yǒu kòng ma?', english: 'Are you free on the weekend?' },
    },
    {
        id: '202-word-dianying',
        kind: 'word',
        hanzi: '电影',
        pinyin: 'diànyǐng',
        meaning: 'movie',
        emoji: '🎬',
        example: { hanzi: '我们去看电影吧。', pinyin: 'Wǒmen qù kàn diànyǐng ba.', english: "Let's go see a movie." },
    },
    {
        id: '202-listen-dianying',
        kind: 'listenRepeat',
        hanzi: '我们去看电影吧。',
        pinyin: 'Wǒmen qù kàn diànyǐng ba.',
        english: "Let's go see a movie.",
        infoText: 'Just say it back — this one is not scored.',
        graded: false,
        grades: [],
    },
    {
        id: '202-quiz-movie',
        kind: 'quiz',
        mode: 'text',
        prompt: "Which one means 'to watch a movie'?",
        options: [
            { hanzi: '打篮球', pinyin: 'dǎ lánqiú' },
            { hanzi: '看电影', pinyin: 'kàn diànyǐng' },
            { hanzi: '听音乐', pinyin: 'tīng yīnyuè' },
            { hanzi: '去公园', pinyin: 'qù gōngyuán' },
        ],
        correctIndex: 1,
    },
    {
        id: '202-convo-weekend-audio',
        kind: 'conversation',
        mode: 'listenNoCharacters',
        title: 'Listen first — no text',
        lines: weekendConversationLines,
    },
    {
        id: '202-convo-weekend',
        kind: 'conversation',
        mode: 'listen',
        title: 'What are you doing this weekend?',
        lines: weekendConversationLines,
    },
    {
        id: '202-convo-weekend-listenspeak',
        kind: 'conversation',
        mode: 'listenSpeak',
        title: "Listen, then take Dawei's lines",
        lines: weekendConversationLines,
    },
    {
        id: '202-speak-weekend',
        kind: 'speak',
        graded: true,
        heroHanzi: '你周末想做什么？',
        heroPinyin: 'Nǐ zhōumò xiǎng zuò shénme?',
        speaker: { nameHanzi: '小美', nameEnglish: 'Xiaomei' },
        prompt: { hanzi: '这个周末你想做什么？', english: 'What do you want to do this weekend?' },
        starters: ['我想去……', '我们一起……吧', '我打算……'],
        grades: charGrades('我 wǒ ggg | 想 xiǎng ggb xiáng | 去 qù gog | 看 kàn ggb kǎn | 电 diàn ggg | 影 yǐng gbo yǐn'),
    },
    {
        id: '202-word-gongyuan',
        kind: 'word',
        hanzi: '公园',
        pinyin: 'gōngyuán',
        meaning: 'park',
        emoji: '🌳',
        example: { hanzi: '公园里人很多。', pinyin: 'Gōngyuán lǐ rén hěn duō.', english: 'There are a lot of people in the park.' },
    },
    {
        id: '202-quiz-together',
        kind: 'quiz',
        mode: 'text',
        prompt: "Which one means 'together'?",
        options: [
            { hanzi: '已经', pinyin: 'yǐjīng' },
            { hanzi: '以前', pinyin: 'yǐqián' },
            { hanzi: '一起', pinyin: 'yìqǐ' },
            { hanzi: '一定', pinyin: 'yídìng' },
        ],
        correctIndex: 2,
    },
    {
        id: '202-word-dasuan',
        kind: 'word',
        hanzi: '打算',
        pinyin: 'dǎsuàn',
        meaning: 'to plan / to intend',
        emoji: '📝',
        example: { hanzi: '你打算做什么？', pinyin: 'Nǐ dǎsuàn zuò shénme?', english: 'What do you plan to do?' },
    },
    {
        id: '202-convo-weekend-read',
        kind: 'conversation',
        mode: 'readSpeak',
        title: 'Read your lines aloud',
        lines: weekendConversationLines,
    },
    {
        id: '202-convo-weekend-memory',
        kind: 'conversation',
        mode: 'speakOnly',
        title: 'Now from memory',
        lines: weekendConversationLines,
    },
    {
        id: '202-speak-plan',
        kind: 'speak',
        graded: true,
        heroHanzi: '说说你的周末计划',
        heroPinyin: 'Shuōshuo nǐ de zhōumò jìhuà',
        speaker: { nameHanzi: '大伟', nameEnglish: 'Dawei' },
        prompt: { hanzi: '这个周末你打算做什么？', english: 'What do you plan to do this weekend?' },
        starters: ['我打算……', '星期六我要……', '我想和朋友……'],
        grades: charGrades('我 wǒ ggg | 打 dǎ ggg | 算 suàn ggo | 去 qù ggg | 公 gōng ggg | 园 yuán gob yuǎn'),
    },
    {
        id: '202-speak-free',
        kind: 'speak',
        graded: false,
        heroHanzi: '随便说说吧！',
        heroPinyin: 'Suíbiàn shuōshuo ba!',
        speaker: { nameHanzi: '大伟', nameEnglish: 'Dawei' },
        prompt: { hanzi: '你喜欢周末做什么？', english: 'What do you like doing on weekends?' },
        starters: ['我喜欢……', '我常常……'],
        hint: 'Say anything you like — this one is not scored.',
        grades: [],
    },
    {
        id: '202-summary',
        kind: 'summary',
        title: 'Your speaking this lesson',
        sentences: [
            {
                english: 'I want to go see a movie.',
                attempts: [
                    charGrades('我 wǒ ggg | 想 xiǎng ggg | 去 qù ggg | 看 kàn ggg | 电 diàn ggg | 影 yǐng ggo'),
                    charGrades('我 wǒ ggg | 想 xiǎng ggb xiáng | 去 qù gog | 看 kàn ggb kǎn | 电 diàn ggg | 影 yǐng gbo yǐn'),
                ],
            },
            {
                english: 'I plan to go to the park.',
                attempts: [
                    charGrades('我 wǒ ggg | 打 dǎ ggg | 算 suàn ggo | 去 qù ggg | 公 gōng ggg | 园 yuán gob yuǎn'),
                ],
            },
        ],
    },
    {
        id: '202-choose-next',
        kind: 'chooseNext',
        prompt: 'Nice work! Where would you like to go next?',
        options: [
            { emoji: '🧭', title: 'Lesson 6: Asking for Directions', detail: 'Next lesson in Chinese 2' },
            { emoji: '🔁', title: 'Practice this lesson again', detail: 'Replay the weekend dialogue' },
            { emoji: '🎯', title: 'Tone Pairs Challenge', detail: 'Pronunciation Lab' },
        ],
    },
];

// ---------------------------------------------------------------------------
// Generic fallback pool (greetings/basics — fits any lesson), cycled to fill
// every position that has no authored content.
// ---------------------------------------------------------------------------

const genericPool: LessonExercise[] = [
    {
        id: 'pool-note-start',
        kind: 'image',
        caption: 'We will learn a few words first, then use them in a conversation.',
        chineseHanzi: '我们开始上课吧！',
        chinesePinyin: 'Wǒmen kāishǐ shàng kè ba!',
    },
    {
        id: 'pool-word-nihao',
        kind: 'word',
        hanzi: '你好',
        pinyin: 'nǐ hǎo',
        meaning: 'hello',
        emoji: '😊',
        example: { hanzi: '你好，我是小美。', pinyin: 'Nǐ hǎo, wǒ shì Xiǎoměi.', english: "Hello, I'm Xiaomei." },
    },
    {
        id: 'pool-quiz-thanks',
        kind: 'quiz',
        mode: 'text',
        prompt: "Which one means 'thank you'?",
        options: [
            { hanzi: '再见', pinyin: 'zàijiàn' },
            { hanzi: '谢谢', pinyin: 'xièxie' },
            { hanzi: '你好', pinyin: 'nǐ hǎo' },
            { hanzi: '请', pinyin: 'qǐng' },
        ],
        correctIndex: 1,
    },
    {
        id: 'pool-convo-hello',
        kind: 'conversation',
        mode: 'listen',
        title: 'Saying hello',
        lines: [
            {
                speakerHanzi: '老师', speakerEnglish: 'Teacher', side: 'left',
                hanzi: '你好！你叫什么名字？', pinyin: 'Nǐ hǎo! Nǐ jiào shénme míngzi?',
                english: "Hello! What's your name?",
            },
            {
                speakerHanzi: '学生', speakerEnglish: 'Student', side: 'right',
                hanzi: '你好，我叫大伟。', pinyin: 'Nǐ hǎo, wǒ jiào Dàwěi.',
                english: 'Hello, my name is Dawei.',
                isStudent: true,
                blankHanzi: '你好，我叫__。',
                grades: charGrades('你 nǐ ggg | 好 hǎo ggg | 我 wǒ ggg | 叫 jiào ggo | 大 dà ggg | 伟 wěi gob wèi'),
            },
        ],
    },
    {
        id: 'pool-match-greetings',
        kind: 'match',
        prompt: 'Match the characters with their pinyin.',
        leftMatch: 'character',
        rightMatch: 'pinyin',
        pairs: [
            { hanzi: '你好', pinyin: 'nǐ hǎo', english: 'hello', emoji: '😊' },
            { hanzi: '谢谢', pinyin: 'xièxie', english: 'thank you', emoji: '🙏' },
            { hanzi: '再见', pinyin: 'zàijiàn', english: 'goodbye', emoji: '👋' },
        ],
    },
    {
        id: 'pool-word-zaijian',
        kind: 'word',
        hanzi: '再见',
        pinyin: 'zàijiàn',
        meaning: 'goodbye',
        emoji: '👋',
        example: { hanzi: '明天见，再见！', pinyin: 'Míngtiān jiàn, zàijiàn!', english: 'See you tomorrow — bye!' },
    },
    {
        id: 'pool-listen-nihao',
        kind: 'listenRepeat',
        hanzi: '你好，很高兴认识你。',
        pinyin: 'Nǐ hǎo, hěn gāoxìng rènshi nǐ.',
        english: 'Hello, nice to meet you.',
        infoText: 'Listen once, then say it back with the same tones.',
        graded: true,
        grades: charGrades('你 nǐ ggg | 好 hǎo ggo | 很 hěn ggg | 高 gāo ggg | 兴 xìng ggb xīng | 认 rèn ggg | 识 shi gog | 你 nǐ ggg'),
    },
    {
        id: 'pool-quiz-three',
        kind: 'quiz',
        mode: 'text',
        prompt: "Which one means 'three'?",
        options: [
            { hanzi: '六', pinyin: 'liù' },
            { hanzi: '三', pinyin: 'sān' },
            { hanzi: '十', pinyin: 'shí' },
            { hanzi: '九', pinyin: 'jiǔ' },
        ],
        correctIndex: 1,
    },
    {
        id: 'pool-speak-name',
        kind: 'speak',
        graded: true,
        heroHanzi: '你叫什么名字？',
        heroPinyin: 'Nǐ jiào shénme míngzi?',
        speaker: { nameHanzi: '老师', nameEnglish: 'Teacher' },
        prompt: { hanzi: '你好！你叫什么名字？', english: "Hello! What's your name?" },
        starters: ['我叫……', '我的名字是……'],
        grades: charGrades('我 wǒ ggg | 叫 jiào ggo | 大 dà ggg | 伟 wěi gob wèi'),
    },
];

// Always returns exactly lesson.totalPrompts items, so completedPrompts doubles
// as the resume index. Authored content fills the lesson's final positions;
// everything else cycles the generic pool (ids re-keyed per position).
const authoredByLesson: Record<number, LessonExercise[]> = {
    201: orderingAtRestaurantExercises,
    202: weekendPlansExercises,
};

export function getLessonExercises(lesson: StudentLesson): LessonExercise[] {
    const authored = authoredByLesson[lesson.id] ?? [];
    const authoredStart = Math.max(0, lesson.totalPrompts - authored.length);
    const exercises: LessonExercise[] = [];
    for (let i = 0; i < lesson.totalPrompts; i++) {
        if (i >= authoredStart && i - authoredStart < authored.length) {
            exercises.push(authored[i - authoredStart]);
        } else {
            const pooled = genericPool[i % genericPool.length];
            exercises.push({ ...pooled, id: `${lesson.id}-${i}-${pooled.id}` });
        }
    }
    return exercises;
}

// ---------------------------------------------------------------------------
// Speaking feedback helpers
// ---------------------------------------------------------------------------

const gradePoints: Record<SoundGrade, number> = { good: 100, ok: 65, bad: 30 };

export function speakingScore(grades: CharacterGrade[]): number {
    if (grades.length === 0) {
        return 0;
    }
    const total = grades.reduce((sum, g) => sum + gradePoints[g.initial] + gradePoints[g.final] + gradePoints[g.tone], 0);
    return Math.round(total / (grades.length * 3));
}

export function scoreMessage(score: number): { title: string; subtitle: string } {
    if (score >= 90) {
        return { title: 'Excellent!', subtitle: 'Your pronunciation is really coming along.' };
    }
    if (score >= 75) {
        return { title: 'Great job!', subtitle: 'Almost there — check the details to polish a few sounds.' };
    }
    if (score >= 60) {
        return { title: 'Good effort!', subtitle: 'Listen to the model again and give it another try.' };
    }
    return { title: 'Keep practicing!', subtitle: "Play the model audio and copy it slowly — you'll get it." };
}

// Re-records nudge grades up one notch (every bad → ok, one ok → good) so
// trying again visibly pays off in the demo.
export function improveGrades(grades: CharacterGrade[]): CharacterGrade[] {
    let promotedOk = false;
    const improve = (grade: SoundGrade): SoundGrade => {
        if (grade === 'bad') {
            return 'ok';
        }
        if (grade === 'ok' && !promotedOk) {
            promotedOk = true;
            return 'good';
        }
        return grade;
    };
    return grades.map(grade => {
        const improved = { ...grade, initial: improve(grade.initial), final: improve(grade.final), tone: improve(grade.tone) };
        if (improved.initial === 'good' && improved.final === 'good' && improved.tone === 'good') {
            improved.pinyinSpoken = improved.pinyinExpected;
        }
        return improved;
    });
}
